import React, { useState, useEffect } from 'react';
import { Avatar, Badge, ToggleSwitch } from 'components/common';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaSpinner, FaCamera, FaEnvelope, FaMapMarkerAlt, FaStore, FaClock,
  FaSignInAlt, FaShieldAlt, FaRegCalendarAlt,
} from 'react-icons/fa';
import { MdMeetingRoom } from 'react-icons/md';
import { updateUser } from 'redux/authorization/userSlice';
import UserDetailChangeAvatar from './UserDetailChangeAvatar';

// ── Consistent typography helpers ──────────────────────────────────────────────
// Label: tw-text-xs tw-text-slate-500 · Value: tw-text-sm tw-font-medium tw-text-slate-800
const Field = ({ label, value, isLink, mono }) => (
  <div className="tw-min-w-0">
    <p className="tw-text-xs tw-text-slate-500 tw-mb-0.5">{label}</p>
    {isLink && value ? (
      <a href={`mailto:${value}`} className="tw-text-sm tw-font-medium tw-text-blue-600 tw-no-underline hover:tw-underline tw-break-all">{value}</a>
    ) : (
      <p className={`tw-text-sm tw-font-medium tw-text-slate-800 tw-mb-0 tw-break-words ${mono ? 'tw-font-mono' : ''}`}>
        {value === 0 ? 0 : (value || '—')}
      </p>
    )}
  </div>
);
Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLink: PropTypes.bool,
  mono: PropTypes.bool,
};

const InfoCard = ({ icon: Icon, title, accent = 'tw-bg-blue-50 tw-text-blue-600', children }) => (
  <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-bg-white tw-flex tw-flex-col tw-h-full">
    <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100 tw-flex tw-items-center tw-gap-2.5">
      <div className={`tw-w-8 tw-h-8 tw-rounded tw-flex tw-items-center tw-justify-center tw-shrink-0 ${accent}`}>
        <Icon size={14} />
      </div>
      <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-slate-800">{title}</h3>
    </div>
    <div className="tw-p-4 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 tw-flex-1">{children}</div>
  </div>
);
InfoCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  accent: PropTypes.string,
  children: PropTypes.node,
};

const MetaItem = ({ icon: Icon, children }) => (
  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-slate-500">
    {Icon && <Icon size={11} className="tw-text-slate-400" />} {children}
  </span>
);
MetaItem.propTypes = { icon: PropTypes.elementType, children: PropTypes.node };

const UserDetailInfo = ({ user }) => {
  const dispatch = useDispatch();
  const [showModalAvatar, setShowModalAvatar] = useState(false);
  const [meetingAdmin, setMeetingAdmin] = useState(!!user?.admin_meeting_room);
  const [viewAllUnits, setViewAllUnits] = useState(!!user?.all_sales_units);
  const [saving, setSaving] = useState(false);
  const { isLoading } = useSelector(state => state.authorization_user);

  // Keep local toggle state in sync when the user record loads / changes.
  useEffect(() => {
    setMeetingAdmin(!!user?.admin_meeting_room);
    setViewAllUnits(!!user?.all_sales_units);
  }, [user?.admin_meeting_room, user?.all_sales_units]);

  const handleFlag = async (field, next, setLocal) => {
    setLocal(next); // optimistic
    setSaving(true);
    try {
      await dispatch(updateUser({ userId: user.id, data: { [field]: next } })).unwrap();
      toast.success('Updated successfully');
    } catch (error) {
      setLocal(!next); // revert on failure
      toast.error(error?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  };

  if (!user) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-py-12">
        <FaSpinner className="tw-animate-spin tw-text-blue-500 tw-text-2xl tw-mb-2" />
        <p className="tw-text-sm tw-text-slate-500 tw-mb-0">Loading user information…</p>
      </div>
    );
  }

  const salesUnit = user.sales_unit?.name
    ? `${user.sales_unit.name}${user.sales_unit?.description ? ` · ${user.sales_unit.description}` : ''}`
    : null;

  return (
    <div>
      {/* ── Hero identity card (focal point) ── */}
      <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm tw-bg-white tw-mb-5">
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-5 tw-p-5">
          {/* Avatar with gradient ring + change button (the accent / focal point) */}
          <div className="tw-relative tw-shrink-0 tw-mx-auto sm:tw-mx-0">
            <div className="tw-w-20 tw-h-20 tw-rounded-xl tw-p-[3px] tw-bg-gradient-to-br tw-from-blue-500 tw-to-violet-600 tw-shadow-md">
              <div className="tw-w-full tw-h-full tw-rounded-xl tw-bg-white tw-p-0.5 tw-flex tw-items-center tw-justify-center">
                <Avatar src={user.avatar_url} name={user.name || user.full_name} size="3xl" variant="primary" />
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowModalAvatar(true)}
              title="Change avatar"
              aria-label="Change avatar"
              className="tw-absolute tw-right-0 tw-bottom-0 tw-w-7 tw-h-7 tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 tw-shadow tw-flex tw-items-center tw-justify-center tw-text-blue-600 hover:tw-bg-blue-50 tw-cursor-pointer"
            >
              <FaCamera size={11} />
            </button>
          </div>

          {/* Identity */}
          <div className="tw-flex-1 tw-min-w-0 tw-text-center sm:tw-text-left">
            <div className="tw-flex tw-items-center tw-justify-center sm:tw-justify-start tw-gap-2 tw-flex-wrap">
              <h2 className="tw-text-xl tw-font-bold tw-text-slate-900 tw-mb-0 tw-leading-tight">{user.name || '—'}</h2>
              <Badge variant={user.active ? 'success' : 'danger'} size="sm">{user.active ? 'Active' : 'Inactive'}</Badge>
            </div>
            {user.full_name && <p className="tw-text-sm tw-text-slate-500 tw-mb-2 tw-mt-0.5">{user.full_name}</p>}

            <div className="tw-flex tw-flex-wrap tw-justify-center sm:tw-justify-start tw-gap-x-4 tw-gap-y-1.5 tw-text-xs">
              <span className="tw-inline-flex tw-items-center tw-font-mono tw-font-semibold tw-text-slate-600 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-sm">
                {user.user_code || '—'}
              </span>
              {user.email && <MetaItem icon={FaEnvelope}>{user.email}</MetaItem>}
              {user.site?.name && <MetaItem icon={FaMapMarkerAlt}>{user.site.name}</MetaItem>}
              {salesUnit && <MetaItem icon={FaStore}>{salesUnit}</MetaItem>}
              {user.time_zone && <MetaItem icon={FaClock}>{user.time_zone}</MetaItem>}
            </div>
          </div>

          {/* Attribute flags — toggles, right aligned to balance the row */}
          <div className="tw-flex tw-flex-col tw-gap-2.5 tw-shrink-0 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3 tw-min-w-[220px]">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-slate-600">
                <MdMeetingRoom size={13} className="tw-text-slate-400" /> Meeting Room Admin
              </span>
              <ToggleSwitch
                size="sm"
                value={meetingAdmin}
                disabled={saving}
                onChange={(next) => handleFlag('admin_meeting_room', next, setMeetingAdmin)}
              />
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-slate-600">
                <FaStore size={12} className="tw-text-slate-400" /> View all units
              </span>
              <ToggleSwitch
                size="sm"
                value={viewAllUnits}
                disabled={saving}
                onChange={(next) => handleFlag('all_sales_units', next, setViewAllUnits)}
              />
            </div>
            {(isLoading || saving) && (
              <div className="tw-flex tw-justify-end">
                <FaSpinner className="tw-animate-spin tw-text-blue-500" size={12} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Detail sections — balanced 2-column grid ── */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 tw-items-start">
        <InfoCard icon={FaSignInAlt} title="Sign-in Activity" accent="tw-bg-blue-50 tw-text-blue-600">
          <Field label="Current Sign In" value={fmt(user.current_sign_in_at)} />
          <Field label="Last Sign In" value={fmt(user.last_sign_in_at)} />
          <Field label="Sign In Count" value={user.sign_in_count ?? '—'} />
          <Field label="Current IP" value={user.current_sign_in_ip || '—'} />
          <Field label="Remember Created" value={fmt(user.remember_created_at)} />
        </InfoCard>

        <InfoCard icon={FaShieldAlt} title="Account Security" accent="tw-bg-red-50 tw-text-red-600">
          <Field label="Failed Attempts" value={user.failed_attempts ?? 0} />
          <Field label="Locked Date" value={fmt(user.locked_at)} />
          <Field label="Provider" value={user.provider || '—'} />
          <Field label="UID" value={user.uid || '—'} mono />
          <Field label="Reset Password Sent" value={fmt(user.reset_password_sent_at)} />
          <Field label="Reset Token" value={user.reset_password_token ? '••••••••' : '—'} mono />
          <Field label="Unlock Token" value={user.unlock_token ? '••••••••' : '—'} mono />
        </InfoCard>

        <InfoCard icon={FaRegCalendarAlt} title="Creation & Updates" accent="tw-bg-emerald-50 tw-text-emerald-600">
          <Field label="Created By" value={user.created_by || '—'} />
          <Field label="Created Date" value={fmt(user.created_at)} />
          <Field label="Updated Date" value={fmt(user.updated_at)} />
        </InfoCard>

        <InfoCard icon={FaEnvelope} title="Email Confirmation" accent="tw-bg-violet-50 tw-text-violet-600">
          <Field label="Confirmed Date" value={fmt(user.confirmed_at)} />
          <Field label="Confirmation Sent" value={fmt(user.confirmation_sent_at)} />
          <Field label="Unconfirmed Email" value={user.unconfirmed_email || '—'} />
        </InfoCard>
      </div>

      {showModalAvatar && (
        <UserDetailChangeAvatar
          user={user}
          showModal={showModalAvatar}
          setshowModal={setShowModalAvatar}
        />
      )}
    </div>
  );
};

UserDetailInfo.propTypes = {
  user: PropTypes.object.isRequired,
};

export default UserDetailInfo;
